











import { Either, left, right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Prop } from 'vue-property-decorator'

import { log } from '@/log'
import { CommerceSalesOrder } from '@/models/api/commerce-sales-order'
import { CookieService } from '@/services/cookie'
import ExternalPaymentCallbackView from '@ajax/vue/components/organisms/external-payment/ExternalPaymentCallbackView.vue'
import { HlAppViewModelBuilder } from '@ajax/vue/components/organisms/external-payment/model/hl-app-view-model'
import { DefaultViewModelBuilder, ViewModel } from '@ajax/vue/components/organisms/external-payment/model/view-model'
import { HlAppModeMixin } from '@spa/mixins/hl-app-mode'
import { OrderHistoryApiService } from '@spa/services/api/orderHistory'
import { fromNullable } from 'fp-ts/lib/Option'

@Component({
  components: {
    ExternalPaymentCallbackView,
  },
})
export default class ExternalPaymentBrowserBack extends Mixins<HlAppModeMixin>(HlAppModeMixin) {
  @Prop({ default: '' })
  orderId: string

  readonly orderHistoryClient = new OrderHistoryApiService()
  viewModel: ViewModel | null = null
  order: CommerceSalesOrder | null = null

  async created() {
    this.viewModel = await fromNullable(this.orderId || null).foldL<Promise<ViewModel>>(
      () => {
        log.error(`orderId is not provided. Usual cancel view is displayed as fallback.`)

        return Promise.resolve(this.setupViewModelForCancel())
      },
      orderId => this.setupViewModelForOrderId(orderId)
    )
  }

  async setupViewModelForOrderId(orderId: string): Promise<ViewModel> {
    const result = await this.fetchOrderDetail(orderId)

    if (result.isLeft()) {
      log.error(`Order (${orderId}) is not found. Usual cancel view is displayed as fallback.`, result.value)

      return Promise.resolve(this.setupViewModelForCancel())
    }

    switch (result.value.salesOrderStatus) {
      case 'payment-done':
        log.info(`Order is already payment-done status.`)

        return Promise.resolve(this.setupViewModelForPaymentDone())
      default:
        return Promise.resolve(this.setupViewModelForCancel())
    }
  }

  setupViewModelForCancel(): ViewModel {
    if (this.forHlApp) {
      return HlAppViewModelBuilder.forPaymentCanceled()
        .withReorderAlertSectionOnCancel()
        .buildWithHlAppReorder()
    }

    const builder = DefaultViewModelBuilder.forPaymentCanceled()
      .withReorderAlertSectionOnCancel()

    return this.userIsRegistered ?
      builder.buildWithReorderLink() :
      builder.buildWithReorderViaLoginLink()
  }

  setupViewModelForPaymentDone(): ViewModel {
    if (this.forHlApp) {
      return HlAppViewModelBuilder.forPaymentSuccessfullyDone()
        .addCheckOrderHistoryErrorMessage()
        .addGuideToOrderHistoryErrorMessage()
        .withCheckOrderHistoryAlertSection()
        .buildWithHlAppOrderHistory()
    }

    const builder = DefaultViewModelBuilder.forPaymentSuccessfullyDone()
      .addCheckOrderHistoryErrorMessage()
      .addGuideToOrderHistoryErrorMessage()
      .withCheckOrderHistoryAlertSection()

    return this.userIsRegistered ?
      builder.buildWithOrderHistoryLink() :
      builder.buildWithOrderHistoryViaLoginLink()
  }

  private get userIsRegistered(): boolean {
    return CookieService.isRegistered()
  }

  private get forHlApp(): boolean {
    return this.hlAppMode.isViewedInHlAppWebView
  }

  private async fetchOrderDetail(orderId: string): Promise<Either<Error, CommerceSalesOrder>> {
    const result = await this.orderHistoryClient.get(orderId)

    if (result.isLeft()) {
      return left(result.value)
    }

    return right(new CommerceSalesOrder(_.get(result.value, 'body.data')))
  }
}
